/**
 * Everything the statically hosted page has to learn from its own URL.
 *
 * As an MFE the app is handed an instance id and the `propertyId` / `groupId`
 * parameters by the Evo host. A static page is not mounted by anything, so the
 * same values arrive in its URL:
 *
 *   https://<host>/<instanceId>/?propertyId=12345
 *
 * The leading path segment matches how the Evo menu item is built (the
 * Application Subscription Id Evo passes as `instanceId`), and is optional;
 * a query parameter naming the same thing is accepted too, under any of the
 * spellings the platform uses and in any position of the path.
 */

export type UrlContext = {
  instanceId: string;
  parameters: Record<string, string>;
};

const PARAMETER_NAMES = ['propertyId', 'groupId'] as const;

const INSTANCE_QUERY_NAMES = [
  'instanceid',
  'instance_id',
  'subscriptionid',
  'subscription_id',
  'applicationsubscriptionid',
  'appsubscriptionid',
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readInstanceId(url: URL) {
  for (const [name, value] of url.searchParams) {
    if (value && INSTANCE_QUERY_NAMES.includes(name.toLowerCase())) return value;
  }

  const segments = url.pathname.split('/').filter(Boolean);
  return segments.find((segment) => UUID.test(segment)) ?? segments[0] ?? null;
}

export function readUrlContext(): UrlContext {
  const url = new URL(window.location.href);
  const parameters: Record<string, string> = {};

  for (const name of PARAMETER_NAMES) {
    const value = url.searchParams.get(name);
    if (value) parameters[name] = value;
  }

  return { instanceId: readInstanceId(url) ?? 'default', parameters };
}

/**
 * Reads a value from the query string, remembering it for later reloads and
 * removing it from the address bar so it is not shared by accident.
 */
function readStickyParam(queryName: string, storageKey: string) {
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get(queryName);

  if (fromQuery) {
    sessionStorage.setItem(storageKey, fromQuery);
    url.searchParams.delete(queryName);
    window.history.replaceState({}, '', url.toString());
    return fromQuery;
  }

  return sessionStorage.getItem(storageKey);
}

/** The access token to present as the app's bearer token, outside Evo only. */
export function readStandaloneToken() {
  return readStickyParam('access_token', 'chatobi.standalone.accessToken');
}

/** Optional `?api_base_url=` override, outside Evo only. */
export function readStandaloneApiBaseUrl() {
  return readStickyParam('api_base_url', 'chatobi.standalone.apiBaseUrl');
}

/** True when the page is not embedded in a parent frame, so there is no Evo host. */
export function isStandalone() {
  try {
    return window.parent === window;
  } catch {
    // Cross-origin parent: we are embedded.
    return false;
  }
}
